import { YStack, Form, XStack, Text, Spinner } from "tamagui";
import FormItem from "@components/form/FormItem";
import { MdCheck } from "react-icons/md";
import { useForm, FormProvider, FieldErrors } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import FormQuantity from "@components/product/FormQuantity";
import Subtotal from "@components/product/Subtotal";
import { RiShoppingBag3Line } from "react-icons/ri";
import { colors } from "@styles/colors";
import { useOrder, Product } from "@utils/store/cart/useOrder";
import { GasCard, SkinBottomBar, isGasSkinV2 } from "./skin";
import api, { handleRequest } from "@services/axios/apiManager";
import { AxiosError, AxiosResponse } from "axios";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { useUser } from "@utils/store/user/useUser";
import { buildXContextHeader } from "@utils/api/headers";
import { addProductItem, updateProductItem, updateDraftOrder } from "@components/product/API/handleProductAPI";
import { getCustomerDeliveryList, CustomerDelivery } from "@components/delivery/API/handleDeliveryAPI";
import { getDraftErrorMessage } from "@utils/api/draftErrorMessage";
import { parseFloorToInt } from "@utils/func/floor";
import toast from "react-hot-toast";
import { useEffect, useRef, useState } from "react";
import { track } from "@lib/tracker";

const PRESET_USAGE_LOCATIONS = ["瓦斯爐", "熱水器", "烘衣機", "暖氣", "壁爐"] as const;

/** 使用位置（appliance type）— 對齊 BE customer_delivery.usage_location */
const FORM_USAGE_LOCATION = {
  itemType: "radio",
  required: true,
  data: {
    id: "usage_location",
    label: "使用位置",
    placeholder: "",
    value: "",
    otherInfo: "",
    options: [...PRESET_USAGE_LOCATIONS.map((v) => ({ label: v, value: v })), { label: "其他", value: "其他" }],
  },
};

/** 樓層 — 對齊 BE customer_delivery.floor（int，地下為負） */
const FORM_FLOOR = {
  itemType: "radio",
  required: true,
  data: {
    id: "floor",
    label: "樓層",
    placeholder: "",
    value: "",
    otherInfo: "",
    options: [
      { label: "B1", value: "B1" },
      { label: "1F", value: "1F" },
      { label: "2F", value: "2F" },
      { label: "3F", value: "3F" },
      { label: "4F", value: "4F" },
      { label: "5F", value: "5F" },
      { label: "6F+", value: "其他" },
    ],
  },
};

/** 電梯 — 對齊 BE customer_delivery.is_elevator */
const FORM_ELEVATOR = {
  itemType: "radio",
  required: true,
  data: {
    id: "is_elevator",
    label: "電梯",
    placeholder: "",
    value: true,
    otherInfo: "",
    options: [
      { label: "有電梯", value: true },
      { label: "無電梯", value: false },
    ],
  },
};

/** BE 回傳的 floor int → form value（反向 mapping）。 */
function floorIntToFormValue(floorInt: number): { floor: string; floor_other: string } {
  if (floorInt === -1) return { floor: "B1", floor_other: "" };
  if (floorInt === 0) return { floor: "1F", floor_other: "" };
  if (floorInt >= 1 && floorInt <= 5) return { floor: `${floorInt}F`, floor_other: "" };
  const other = floorInt < 0 ? `B${-floorInt}` : `${floorInt}`;
  return { floor: "其他", floor_other: other };
}

/** BE 回傳的 usage_location → form value。命中 preset 即原值；否則 "其他" + 自訂。 */
function usageLocationToFormValue(loc: string): { usage_location: string; usage_location_other: string } {
  if ((PRESET_USAGE_LOCATIONS as readonly string[]).includes(loc)) {
    return { usage_location: loc, usage_location_other: "" };
  }
  return { usage_location: "其他", usage_location_other: loc };
}

/** 兩個安裝位「內容」是否相同（用途+樓層+電梯+備註，與 dedup key 一致）。 */
function sameDeliveryContent(
  a: { usage_location: string; floor: number; is_elevator: boolean; note: string | null },
  b: { usage_location: string; floor: number; is_elevator: boolean; note: string | null },
): boolean {
  return (
    a.usage_location === b.usage_location &&
    a.floor === b.floor &&
    a.is_elevator === b.is_elevator &&
    (a.note ?? "").trim() === (b.note ?? "").trim()
  );
}

/** 既有安裝位 radio 顯示文字。 */
function formatDeliveryLabel(d: CustomerDelivery): string {
  const floorLabel = d.floor === 0 ? "1F" : d.floor > 0 ? `${d.floor}F` : `B${-d.floor}`;
  const elevatorLabel = d.is_elevator ? "有電梯" : "無電梯";
  const parts = [floorLabel, elevatorLabel, d.usage_location];
  if (d.note?.trim()) parts.push(`(${d.note.trim()})`);
  return parts.join("・");
}

const NEW_DELIVERY_VALUE = "new";

const InstallPosition = () => {
  const navigate = useNavigate();
  const skinV2 = isGasSkinV2();
  const { chooseProduct, setOrderState, clearChooseProduct, draftOrderId, setDraftOrderId, syncDraft, hideAmountOnPending } = useOrder();
  const { chooseIdentity } = useUser();
  const { cusJwtToken } = useCusAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deliveryList, setDeliveryList] = useState<CustomerDelivery[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [selectedDeliveryId, setSelectedDeliveryId] = useState<string>(NEW_DELIVERY_VALUE);

  const cusAddressId = chooseIdentity?.choose_address?.customer_address_id;
  const xContext = buildXContextHeader(chooseIdentity) || "";

  /* 套用歷史安裝位時，讓下方表單卡出現「已帶入」的提示。
     原本是用 Web Animations API 在卡外閃一圈藍色光環，但光環在這個 App 是警示語彙
     （錯誤、必填未填都長那樣），用來講「值幫你填好了」語意剛好相反。
     改成一道由上往下掃過的柔光（見 all.scss 的 .gas-filled）——方向本身就在說
     「東西是從上面那張卡流下來的」。 */
  const formRef = useRef<HTMLDivElement>(null);
  const [justFilled, setJustFilled] = useState(false);
  const pulseForm = () => {
    // 先關再開：連續套用同一張卡時 class 沒被移除過，CSS 動畫不會重播。
    setJustFilled(false);
    requestAnimationFrame(() => setJustFilled(true));
  };
  useEffect(() => {
    if (!justFilled) return;
    const t = setTimeout(() => setJustFilled(false), 850);
    return () => clearTimeout(t);
  }, [justFilled]);

  //@ 建立 Form
  const formSchema = z
    .object({
      usage_location: z.string().trim().min(1, { message: "請選擇使用位置" }),
      usage_location_other: z.string().trim().optional(),
      floor: z.string().trim().min(1, { message: "請選擇樓層" }),
      floor_other: z.string().trim().optional(),
      is_elevator: z.boolean({ error: "請選擇是否有電梯" }),
      note: z.string().trim().max(100, { message: "備註最多 100 字" }).optional(),
      qty: z.number().min(1, { message: "數量必須在1以上" }).max(20, { message: "一次最多訂購20桶" }),
    })
    .superRefine((values, ctx) => {
      if (values.usage_location === "其他" && (!values.usage_location_other || values.usage_location_other.length === 0)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "請輸入其他用途",
          path: ["usage_location_other"],
        });
      }
      if (values.floor === "其他") {
        if (!values.floor_other || values.floor_other.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "請輸入樓層，例如：5、12、B2、地下1",
            path: ["floor_other"],
          });
        } else if (parseFloorToInt("其他", values.floor_other) === null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "樓層格式錯誤，請輸入例如：5、12、B2、地下1",
            path: ["floor_other"],
          });
        }
      }
    });
  type FormData = z.infer<typeof formSchema>;

  const methods = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      usage_location: "",
      floor: "",
      is_elevator: true,
      note: "",
      qty: 1,
    },
  });

  //@ 初始帶入
  useEffect(() => {
    if (!chooseProduct) return;
    // 編輯既有品項才從 chooseProduct 帶入安裝位；純新增（從「+」帶 GasCylinder，無 line_id / usage）
    // 留空，交給下方歷史位置 default 帶入，避免用 floor||0 算出錯誤的「6F+/0」並與歷史 default 競賽。
    const isEditExisting = !!chooseProduct.line_id || !!chooseProduct.usage_location;
    if (isEditExisting) {
      const usage = usageLocationToFormValue(chooseProduct.usage_location || "");
      const floor = floorIntToFormValue(typeof chooseProduct.floor === "number" ? chooseProduct.floor : 0);
      methods.reset({
        usage_location: usage.usage_location,
        usage_location_other: usage.usage_location_other,
        floor: floor.floor,
        floor_other: floor.floor_other,
        is_elevator: chooseProduct.is_elevator ?? true,
        note: chooseProduct.note ?? "",
        qty: chooseProduct.qty || 1,
      });
    } else {
      methods.reset({
        usage_location: "",
        usage_location_other: "",
        floor: "",
        floor_other: "",
        is_elevator: true,
        note: "",
        qty: chooseProduct.qty || 1,
      });
    }
  }, [chooseProduct]);

  const watchUsage = methods.watch("usage_location");
  const watchFloor = methods.watch("floor");
  const { setValue, reset, getValues } = methods;

  // 只在「使用者實際選了非『其他』的選項」時清掉自訂值；watchUsage 為空（尚未選 / 初始）時不清，
  // 否則會在初始 prefill（reset 設好 other 值）後、用舊的空值 closure 把剛帶入的 B7 等自訂值清掉。
  useEffect(() => {
    if (watchUsage && watchUsage !== "其他") setValue("usage_location_other", "");
  }, [watchUsage, setValue]);

  useEffect(() => {
    if (watchFloor && watchFloor !== "其他") setValue("floor_other", "");
  }, [watchFloor, setValue]);

  const watchUsageOther = methods.watch("usage_location_other");
  const watchFloorOther = methods.watch("floor_other");
  const watchElevator = methods.watch("is_elevator");
  const watchNote = methods.watch("note");

  // 選中既有位置時「就地編輯」：改表單（如樓層）不切換選取，選中的卡片標籤即時反映表單當前值。
  //（送出時帶 customer_delivery_id → BE upsert UPDATE 同一筆，等於把該歷史位置的樓層改成新值。）
  // 表單尚未填整時回 null，fallback 原值。
  const buildLabelFromForm = (): string | null => {
    const usageRaw = watchUsage === "其他" ? (watchUsageOther ?? "").trim() : watchUsage;
    const floorInt = parseFloorToInt(watchFloor, watchFloorOther ?? "");
    if (!usageRaw || floorInt === null) return null;
    return formatDeliveryLabel({
      customer_delivery_id: 0,
      customer_address_id: 0,
      usage_location: usageRaw,
      floor: floorInt,
      is_elevator: watchElevator,
      note: (watchNote ?? "").trim() || null,
    });
  };
  const selectedLiveLabel = selectedDeliveryId !== NEW_DELIVERY_VALUE ? buildLabelFromForm() : null;

  // 套用安裝位清單：去重（同「用途+樓層+電梯+備註」只留一筆）+ 預設選取
  //  - 編輯模式：選中 chooseProduct 對應的安裝位
  //  - 新增模式：預設套用最近用過的一筆並帶入表單 → 有「已套用」感
  const applyDeliveryList = (list: CustomerDelivery[]) => {
    const seen = new Set<string>();
    const deduped = list.filter((d) => {
      const k = `${d.usage_location}|${d.floor}|${d.is_elevator ? 1 : 0}|${(d.note || "").trim()}`;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
    setDeliveryList(deduped);

    if (chooseProduct?.line_id) {
      // 編輯模式：表單已由 chooseProduct 帶入 → 選中對應卡片。
      // 先比 customer_delivery_id；對不上（id 為 null，或同內容多筆被 dedup 收斂成另一張卡）時改比「內容」。
      const editingDeliveryId = chooseProduct.customer_delivery_id;
      const match =
        (editingDeliveryId != null
          ? deduped.find((d) => String(d.customer_delivery_id) === String(editingDeliveryId))
          : undefined) ??
        deduped.find((d) =>
          sameDeliveryContent(d, {
            usage_location: chooseProduct.usage_location ?? "",
            floor: chooseProduct.floor ?? 0,
            is_elevator: !!chooseProduct.is_elevator,
            note: chooseProduct.note ?? null,
          }),
        );
      if (match) setSelectedDeliveryId(String(match.customer_delivery_id));
      return;
    }

    // 新增模式：預設套用最近用過的一筆並帶入表單 → 有「已套用」感
    if (deduped.length > 0) {
      const first = deduped[0];
      setSelectedDeliveryId(String(first.customer_delivery_id));
      const usage = usageLocationToFormValue(first.usage_location);
      const floor = floorIntToFormValue(first.floor);
      reset({
        ...getValues(),
        usage_location: usage.usage_location,
        usage_location_other: usage.usage_location_other,
        floor: floor.floor,
        floor_other: floor.floor_other,
        is_elevator: first.is_elevator,
        note: first.note ?? "",
      });
      setTimeout(pulseForm, 60);
    }
  };

  //@ 既有 customer_delivery 列表：優先用 bootstrap 預載（免 spinner），後端未提供時才 lazy 打 API
  useEffect(() => {
    if (!cusAddressId) return;

    // bootstrap 已預載此地址的安裝位 → 直接套用，免再打 API
    //（products 頁每次進來都會重抓 bootstrap，故清單夠新；新增的安裝位回到 products 即刷新）
    const addressList = Array.isArray(chooseIdentity?.address) ? chooseIdentity.address : [];
    const preloaded = addressList.find((a) => String(a.customer_address_id) === String(cusAddressId))?.install_locations;
    if (Array.isArray(preloaded)) {
      applyDeliveryList(preloaded);
      setListLoading(false);
      return;
    }

    // fallback：後端尚未提供 install_locations（未部署）→ 維持原本 lazy load
    if (!cusJwtToken) return;
    let cancelled = false;
    setListLoading(true);
    (async () => {
      const res = await getCustomerDeliveryList({
        customerAddressId: cusAddressId,
        jwtToken: cusJwtToken,
        xContext,
      });
      if (cancelled) return;
      if (res.state === "success") {
        applyDeliveryList(res.data);
      } else {
        // 拉不到清單時靜默 fallback 為新增模式，不擋使用者下單
        console.warn("getCustomerDeliveryList failed", res.error);
        setDeliveryList([]);
      }
      setListLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // applyDeliveryList 依賴 form helpers，故意不列入 deps 避免每次 render 重跑
  }, [cusAddressId, cusJwtToken, xContext, chooseProduct?.customer_delivery_id, chooseIdentity?.address]);

  //@ 選擇既有安裝位 → 自動 pre-fill 表單欄位
  const handleSelectDelivery = (rawValue: string) => {
    setSelectedDeliveryId(rawValue);
    if (rawValue === NEW_DELIVERY_VALUE) {
      // 重置安裝位相關欄位，保留 qty
      reset({
        ...getValues(),
        usage_location: "",
        usage_location_other: "",
        floor: "",
        floor_other: "",
        is_elevator: true,
        note: "",
      });
      return;
    }
    const target = deliveryList.find((d) => String(d.customer_delivery_id) === rawValue);
    if (!target) return;
    const usage = usageLocationToFormValue(target.usage_location);
    const floor = floorIntToFormValue(target.floor);
    reset({
      ...getValues(),
      usage_location: usage.usage_location,
      usage_location_other: usage.usage_location_other,
      floor: floor.floor,
      floor_other: floor.floor_other,
      is_elevator: target.is_elevator,
      note: target.note ?? "",
    });
    pulseForm();
  };

  //@ 處理滾動至錯誤位置
  const onInvalid = (errors: FieldErrors<FormData>) => {
    const errorKeys = Object.keys(errors);
    if (errorKeys.length > 0) {
      const firstErrorKey = errorKeys[0];
      const element = document.getElementsByName(firstErrorKey)[0] || document.getElementById(firstErrorKey);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      } else {
        const fallbackElement = document.querySelector(`[id*="${firstErrorKey}"]`);
        fallbackElement?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  };

  //@ 建立草稿
  const createDraftOrder = (thisProduct: Product) => {
    const cusPrimarySupplier = chooseIdentity?.primary_supplier?.supplier_id;

    if (!cusAddressId) {
      console.error("Missing customer_address_id");
      setIsSubmitting(false);
      return;
    }

    const type = thisProduct?.gas_type ? "gas" : "commodity";
    const postData = {
      supplier_id: cusPrimarySupplier,
      customer_address_id: cusAddressId,
      [type === "gas" ? "gas_list" : "commodity_list"]: [thisProduct],
    };
    handleRequest({
      setLoading: setIsSubmitting,
      asyncRequest: () =>
        api.post("/app-api/platform-order-drafts", postData, {
          headers: { Authorization: `Bearer ${cusJwtToken}`, "X-Context": xContext },
        }),
      successCallback: (res: AxiosResponse) => {
        const { data } = res.data;
        if (data?.draft_id) {
          track("funnel_step", { step: "installPosition" });
          if (data.draft) {
            syncDraft(data.draft);
          } else {
            setDraftOrderId(data.draft_id);
            setOrderState("loading");
          }
          clearChooseProduct();
          navigate("/gasShop/products");
        }
      },
      errorCallback: (error: unknown) => {
        console.error("createDraftOrder err", error as AxiosError);
        // 與 addProductItem 一致：建立草稿失敗（如後端拒絕樓層等）顯式提示，避免靜默失敗讓品項「不見了」
        toast.error(getDraftErrorMessage(error as AxiosError, "建立訂單失敗"));
        setIsSubmitting(false);
      },
    });
  };

  //@ 下一步流程處理 (新增或更新)
  const handleNext = async (thisProduct: Product) => {
    const type = thisProduct?.gas_type ? "gas" : "commodity";
    setIsSubmitting(true);
    try {
      const isUpdate = thisProduct.line_id;
      const postProductData = JSON.parse(JSON.stringify(thisProduct));

      if (isUpdate) {
        delete postProductData.line_id;
        delete postProductData.totalPrice;
      }

      // 如果當前草稿商品列表為空（可能是清空後重新加入第一個商品），
      // 則使用 updateDraftOrder 來更新整筆訂單，避免 addProductItem 在某些後端邏輯下失效
      const currentDraft = useOrder.getState().draft;
      if (draftOrderId && currentDraft.gas_list?.length === 0) {
        const body = {
          [type === "gas" ? "gas_list" : "commodity_list"]: [postProductData],
        };
        const res = await updateDraftOrder({
          draftId: draftOrderId,
          body,
          jwtToken: cusJwtToken || "",
          xContext,
        });
        if (res?.state === "success") {
          if (!isUpdate) track("funnel_step", { step: "installPosition" });
          if (res.data?.data?.draft) {
            syncDraft(res.data.data.draft);
          } else {
            setOrderState("loading");
          }
          clearChooseProduct();
          navigate("/gasShop/products");
        }
        return;
      }

      const res = isUpdate
        ? await updateProductItem({
            xContext,
            jwtToken: cusJwtToken || "",
            type: type,
            draftId: draftOrderId || "",
            lineId: thisProduct.line_id!,
            body: postProductData,
          })
        : await addProductItem({
            xContext,
            jwtToken: cusJwtToken || "",
            type: type,
            draftId: draftOrderId || "",
            body: thisProduct,
          });

      if (res?.state === "success") {
        if (!isUpdate) track("funnel_step", { step: "installPosition" });
        if (res.data?.data?.draft) {
          syncDraft(res.data.data.draft);
        } else {
          setOrderState("loading");
        }
        clearChooseProduct();
        navigate("/gasShop/products");
      }
    } catch (error) {
      console.error("handleNext error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmit = (data: FormData) => {
    if (!chooseProduct || isSubmitting) return;

    const floorInt = parseFloorToInt(data.floor, data.floor_other);
    if (floorInt === null) {
      // shouldn't happen — schema guards 其他 case; fail loud just in case
      console.error("invalid floor", data);
      return;
    }

    const usageLocation = data.usage_location === "其他" ? (data.usage_location_other ?? "其他") : data.usage_location;
    const note = data.note?.trim() ? data.note.trim() : null;
    const customerDeliveryId = selectedDeliveryId === NEW_DELIVERY_VALUE ? null : Number(selectedDeliveryId);

    const thisProduct = {
      ...chooseProduct,
      // 新 flat shape — 對齊 BE GasItemReqDto
      customer_delivery_id: customerDeliveryId,
      usage_location: usageLocation,
      note,
      floor: floorInt,
      is_elevator: data.is_elevator,
      qty: Number(data.qty),
    } as Product;

    // 如果沒有草稿 ID，且不是編輯既有項目的話，就建立草稿
    const isEditingExisting = thisProduct.line_id && !thisProduct.line_id.startsWith("last-");
    if (!draftOrderId) {
      const postProduct = JSON.parse(JSON.stringify(thisProduct));
      delete postProduct.line_id;
      delete postProduct.totalPrice;
      createDraftOrder(postProduct);
    } else {
      handleNext(thisProduct);
    }
  };

  return (
    <>
      <FormProvider {...methods}>
        <Form onSubmit={methods.handleSubmit(onSubmit, onInvalid)} gap="$4" flex={1}>
          {/* 既有安裝位清單載入中 — 先佔位顯示 spinner，避免清單晚到才插入造成版面跳動 */}
          {listLoading && deliveryList.length === 0 && (
            <GasCard>
              <XStack ai="center" jc="center" gap="$2" py="$3">
                <Spinner size="small" color={colors.primary} />
                <Text fontSize={13} color={colors.gray}>
                  載入安裝位置…
                </Text>
              </XStack>
            </GasCard>
          )}

          {/* 歷史位置 — 曾經套用過的安裝位（清單非空才顯示）。選中：藍底白字 + 打勾，明顯區隔。 */}
          {deliveryList.length > 0 && (
            <GasCard>
              <YStack gap="$2">
                <Text fontSize={14} fontWeight="bold" mb="$1">
                  <Text color={colors.darkRed} fontWeight="700" mr={8}>
                    *
                  </Text>
                  歷史位置
                </Text>
                <YStack gap={10}>
                  {deliveryList.map((d) => {
                    const sel = selectedDeliveryId === String(d.customer_delivery_id);
                    // 選中的卡片即時反映下方表單（改樓層同步顯示新值）；其餘維持原值
                    const label = sel && selectedLiveLabel ? selectedLiveLabel : formatDeliveryLabel(d);
                    return (
                      <XStack
                        key={d.customer_delivery_id}
                        ai="center"
                        jc="space-between"
                        gap={10}
                        px={14}
                        py={13}
                        borderRadius={12}
                        borderWidth={1.5}
                        borderColor={sel ? colors.primary : colors.border}
                        backgroundColor={sel ? colors.primary : colors.light}
                        cursor="pointer"
                        className="gas-pickable"
                        pressStyle={{ opacity: 0.85 }}
                        onPress={() => handleSelectDelivery(String(d.customer_delivery_id))}>
                        <Text fontSize={15} fontWeight={sel ? "bold" : "500"} color={sel ? colors.light : colors.text}>
                          {label}
                        </Text>
                        {sel && (
                          <XStack ai="center" gap={4}>
                            <MdCheck color="#fff" size={18} />
                            <Text fontSize={13} fontWeight="bold" color={colors.light}>
                              已套用
                            </Text>
                          </XStack>
                        )}
                      </XStack>
                    );
                  })}

                  {/* 新增安裝位 */}
                  <XStack
                    ai="center"
                    jc="center"
                    px={14}
                    py={13}
                    borderRadius={12}
                    borderWidth={1.5}
                    borderColor={selectedDeliveryId === NEW_DELIVERY_VALUE ? colors.primary : colors.border}
                    backgroundColor={selectedDeliveryId === NEW_DELIVERY_VALUE ? colors.primaryActive : colors.light}
                    cursor="pointer"
                    className="gas-pickable"
                    pressStyle={{ opacity: 0.85 }}
                    onPress={() => handleSelectDelivery(NEW_DELIVERY_VALUE)}
                    style={{ borderStyle: "dashed" }}>
                    <Text fontSize={15} fontWeight="bold" color={colors.primary}>
                      ＋ 新增安裝位
                    </Text>
                  </XStack>
                </YStack>
              </YStack>
            </GasCard>
          )}

          <div ref={formRef} className={justFilled ? "gas-filled" : undefined} style={{ borderRadius: 16 }}>
          <GasCard>
            <YStack gap="$4">
              {/* 使用位置 */}
              <YStack id={FORM_USAGE_LOCATION.data.id}>
                <FormItem itemType={FORM_USAGE_LOCATION.itemType} required={FORM_USAGE_LOCATION.required} data={FORM_USAGE_LOCATION.data} pill={skinV2} />
                {watchUsage === "其他" && (
                  <YStack mt="$2">
                    <FormItem
                      itemType="input"
                      required={false}
                      data={{
                        id: "usage_location_other",
                        label: "",
                        placeholder: "其他用途,如壁爐、桑拿",
                        value: "",
                        otherInfo: "",
                      }}
                    />
                  </YStack>
                )}
              </YStack>

              {/* 樓層 */}
              <YStack id={FORM_FLOOR.data.id}>
                <FormItem itemType={FORM_FLOOR.itemType} required={FORM_FLOOR.required} data={FORM_FLOOR.data} pill={skinV2} />
                {watchFloor === "其他" && (
                  <YStack mt="$2">
                    <FormItem
                      itemType="input"
                      required={false}
                      data={{
                        id: "floor_other",
                        label: "",
                        placeholder: "例如：5、12、B2、地下1",
                        value: "",
                        otherInfo: "",
                      }}
                    />
                  </YStack>
                )}
              </YStack>

              {/* 電梯 */}
              <YStack id={FORM_ELEVATOR.data.id}>
                <FormItem itemType={FORM_ELEVATOR.itemType} required={FORM_ELEVATOR.required} data={FORM_ELEVATOR.data} pill={skinV2} />
              </YStack>

              {/* 安裝位置備註 */}
              <YStack id="note">
                <Text fontSize={14} fontWeight="bold" mb="$2">
                  安裝位置備註 <Text color="$gray10">選填,例如:廚房、後巷</Text>
                </Text>
                <FormItem
                  itemType="input"
                  required={false}
                  data={{
                    id: "note",
                    label: "",
                    placeholder: "選填,例如:廚房、後巷",
                    value: "",
                    otherInfo: "",
                  }}
                />
              </YStack>
            </YStack>
          </GasCard>
          </div>
          {skinV2 ? (
            <YStack
              className="gas-skin-v2-bottom-fade"
              backgroundColor="transparent"
              mt="auto"
              style={{ position: "sticky", bottom: "0", width: "100%", zIndex: 100 }}>
              <XStack alignItems="center" justifyContent="space-between" px={4} pt={22}>
                <FormQuantity />
                {/* 瓦斯行開啟隱藏金額時，下訂流程不顯示小計 */}
                {!hideAmountOnPending && <Subtotal proPrice={chooseProduct?.unit_price} />}
              </XStack>
              <SkinBottomBar
                secondaryLabel="取消"
                onSecondary={() => navigate("/gasShop/products")}
                primaryLabel={
                  isSubmitting ? "傳送中..." : chooseProduct?.line_id && !chooseProduct.line_id.startsWith("last-") ? "調整" : "加入購物車"
                }
                primaryLoading={isSubmitting}
                primaryDisabled={isSubmitting}
                nested
                onPrimary={() => {
                      // 先讓仍被聚焦的輸入框（樓層/用途/備註自訂值）寫入表單，避免「沒 unfocus 就送出 → 改動沒帶到」
                      (document.activeElement as HTMLElement | null)?.blur?.();
                      requestAnimationFrame(() => methods.handleSubmit(onSubmit, onInvalid)());
                    }}
              />
            </YStack>
          ) : (
            <YStack backgroundColor={colors.bg} pt={8} style={{ position: "sticky", left: "50px", bottom: "0", width: "100%" }}>
              <hr />
              <XStack alignItems="center" justifyContent="space-between" my={10}>
                <FormQuantity />
                {/* 瓦斯行開啟隱藏金額時，下訂流程不顯示小計 */}
                {!hideAmountOnPending && <Subtotal proPrice={chooseProduct?.unit_price} />}
              </XStack>
              <XStack
                className="gasshop-btn-gradient"
                borderRadius={12}
                p={15}
                alignItems="center"
                justifyContent="center"
                opacity={isSubmitting ? 0.5 : 1}
                onPress={
                  isSubmitting
                    ? undefined
                    : () => {
                        // 先讓仍被聚焦的輸入框（樓層/用途/備註自訂值）寫入表單，避免「沒 unfocus 就送出 → 改動沒帶到」
                        (document.activeElement as HTMLElement | null)?.blur?.();
                        requestAnimationFrame(() => methods.handleSubmit(onSubmit, onInvalid)());
                      }
                }>
                <XStack gap={10}>
                  {isSubmitting ? <Spinner size="small" color="white" /> : <RiShoppingBag3Line color="white" size={20} />}
                  <Text color="white" fontSize={16} fontWeight="bold">
                    {isSubmitting ? "傳送中..." : chooseProduct?.line_id && !chooseProduct.line_id.startsWith("last-") ? "調整" : "加入購物車"}
                  </Text>
                </XStack>
              </XStack>
            </YStack>
          )}
        </Form>
      </FormProvider>
    </>
  );
};

export default InstallPosition;
